import type { Chave, Tradutor } from '@vestigo/core/i18n-da-tela';
/**
 * ── AS PALAVRAS DA DOCA — uma só, porque o alcance deixou de ser escolha ───
 *
 * Ordem do dono em 24/09/2026, depois de abrir a aba «Perguntar» e ver os três
 * botões ainda lá:
 *
 * > *«na aba de perguntar nas aulas, eu não quero que o aluno precise escolher,
 * > quero que o agente já traga a resposta sabendo da pergunta e reconheça o
 * > escopo … se um aluno faz uma pergunta o agente verifica se é da aula, e
 * > responde, se não for, ele aborda o conhecimento de todos os cursos,
 * > apontando para algum conteúdo do próprio curso se houver.»*
 *
 * ## O que este ficheiro era, e por que encolheu
 *
 * Era um `Record<EscopoDaPergunta, PalavrasDoAlcance>` com três colunas — uma
 * por botão —, e ele existia para consertar um defeito anterior: a tela tinha
 * *dois ramos para três estados* e dizia «não achei nas aulas deste curso» a
 * quem tinha acabado de alargar a busca para todos.
 *
 * O `Record` estava certo enquanto o aluno escolhia. **Ele deixou de escolher**,
 * e três colunas para um estado só é a mesma doença ao contrário: rótulos que
 * ninguém lê, frases que divergem entre si, e um controlo que o servidor já
 * ignorava desde que o escopo virou opcional em `aluno-assistente.ts`.
 *
 * ## A lição, que é a razão de esta nota ficar
 *
 * **O conserto foi feito no servidor e não na tela, e por isso não estava
 * feito.** O servidor passou a ignorar o escopo; a tela continuou a desenhar os
 * três botões, a prometer uma escolha que não tinha efeito nenhum. O dono viu-o
 * antes de mim, na captura. Enquanto a tela oferecer o gesto, o gesto existe
 * para quem usa — o que o servidor faz com ele é invisível daqui.
 *
 * ## O que as frases podem e não podem afirmar
 *
 * Nenhuma pode dizer ONDE se procurou como se fosse uma fronteira escolhida: não
 * há fronteira. O agente sabe em que aula a pessoa está (o servidor manda-lhe
 * «ONDE O ALUNO ESTÁ»), olha essa aula primeiro e responde do resto quando
 * precisa. A tela diz o que isso entrega — a aula e o minuto, quando houver — e
 * nunca promete um limite.
 */

export interface PalavrasDaDoca {
  /**
   * O nome da PORTA — no botão que a abre e na barra do painel que abriu.
   *
   * Uma palavra só, e a mesma nos dois sítios: é assim que a pessoa liga o
   * gesto ao que apareceu. Ela diz o que ELA faz, e não quem responde.
   *
   * Continua a ser o rótulo ACESSÍVEL da porta — o que o leitor de tela anuncia
   * e o que o teclado encontra —, mesmo depois de a tutora ter ganho nome: ver
   * `nome`, aqui ao lado.
   */
  porta: string;
  /**
   * ── O NOME DA TUTORA ──────────────────────────────────────────────────────
   *
   * Ordem do dono, 01/10/2026: *«coloque uma label no agente de "Neura Tutora"
   * com um chapeuzinho de formando»*.
   *
   * **Isto derruba uma regra que este ficheiro afirmava**, e a prosa de cima foi
   * reescrita em vez de ficar a mentir: o §8.8 proibia nome próprio, pelo
   * argumento de que o aluno está dentro da marca do tutor e assinar a casa de
   * outra pessoa com uma tecnologia não é nosso. O dono decidiu o contrário, e
   * a decisão é dele.
   *
   * O que a decisão NÃO derruba, e continua a valer: nada de «IA», nada de
   * fornecedor, nada de faísca, nada de robô. «Neura Tutora» é uma tutora com
   * nome e capelo — é pessoal de escola, e não assinatura de tecnologia.
   */
  nome: string;
  /** O que o campo pergunta a quem chega. */
  convite: string;
  /** O título do estado vazio. */
  vazio: string;
  /** Quando não há corpo nenhum para mostrar. */
  naoAchei: string;
  /** O que fazer a seguir, dito sem pedir desculpa. */
  saida: string;
}

/**
 * ── AS CHAVES, E POR QUE ELAS NÃO SE CHAMAM `PALAVRAS` ─────────────────────
 *
 * Esta tabela guardava FRASES e passou a guardar CHAVES. O nome mudou com
 * elas, e isso não é estilo: enquanto se chamava `PALAVRAS` e tinha tipo
 * `string`, os quatro sítios de desenho continuaram a imprimi-la crua e o
 * `tsc` calou-se — uma chave é uma string. O aluno leria
 * `assistente.naoAchei` na resposta mais importante desta tela, e foram quatro
 * cercas da `doca/` que o acusaram, não o compilador.
 *
 * Com o nome trocado e o tipo `Chave`, nada que leia isto pode desenhá-lo por
 * acidente: quem quer a FRASE chama `palavrasDaDoca(t)`.
 */
export const CHAVES_DA_DOCA = {
  porta: 'assistente.perguntar',
  /* Serve na aula e fora dela: é o que a pessoa tem na cabeça, e não nomeia
     fronteira nenhuma. */
  convite: 'assistente.oQueFicouSemEntender',
  vazio: 'assistente.pergunteOQueQuiser',
  /* «nas suas aulas» e não «neste curso»: é a verdade do que foi procurado, e
     era exatamente aqui que a versão de dois ramos mentia. */
  naoAchei: 'assistente.naoAchei',
  saida: 'assistente.procureiEmTodos',
} satisfies Record<string, Chave>;

/**
 * O NOME, que não é chave e não vai ser.
 *
 * «Neura Tutora» é nome próprio — uma tutora com nome e capelo —, e nome
 * próprio não se traduz. Fica fora da tabela de chaves de propósito, para
 * ninguém lhe procurar tradução nem a cobrar nas seis línguas.
 */
export const NOME_DA_DOCA = 'Neura Tutora';

/**
 * As palavras desta tela, na língua de quem está a ler.
 *
 * O tradutor é parâmetro OBRIGATÓRIO. Um `t: Tradutor = tradutorDe(null)` por
 * omissão arrastaria o dicionário inteiro para o pedaço de entrada do aluno, e
 * esta obra já pagou isso uma vez.
 */
export function palavrasDaDoca(t: Tradutor): PalavrasDaDoca {
  return {
    porta: t(CHAVES_DA_DOCA.porta),
    nome: NOME_DA_DOCA,
    convite: t(CHAVES_DA_DOCA.convite),
    vazio: t(CHAVES_DA_DOCA.vazio),
    naoAchei: t(CHAVES_DA_DOCA.naoAchei),
    saida: t(CHAVES_DA_DOCA.saida),
  };
}
